import React from 'react';
import BaseModal from './BaseModal';
import WithBaseModal from './WithBaseModal';
import { columnType } from '../../components/ClearingTable/Table';
import { Grid, Select, InputLabel } from '@material-ui/core';
import InputField from '../../components/Input/InputField';
import { getColumnValue } from '../../components/ClearingTable/CleanUpTable';
import TextEditor from '../../components/TextEditor/TextEditor';
import { Cancel as IconUncheck, CheckCircle as IconCheck } from '@material-ui/icons';
import { protoDatTimeObjectToDateWithMilliseconds } from '../../services/ConvertService';

const DEFAULT_FIELD_SIZE = 3;

const groupBy = (xs, key) => {
  return xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

const RowDetailsModal = ({ data, title, open, onClose, id, renderRowDetailsAddOnButtons }) => {
  const groupFields = groupBy(
    data.filter((i) => i.type !== columnType.buttons && i.label !== 'Select All'),
    'detailsGroup'
  );
  const addOnButtons = renderRowDetailsAddOnButtons
  ? renderRowDetailsAddOnButtons(data)
  : [];

  return (
    <BaseModal
      size="Full"
      open={open}
      title={
        <div
          style={{
            textTransform: 'uppercase',
          }}
        >
          {title + ' - Row Details'}
        </div>
      }
      onClose={onClose}
      enableBackdropClose
      // BaseModal SAMPLE CONFIGS
      infoSection={<div>Field values of the selected row.</div>}
      addOnHeaderButtonsSection={addOnButtons}
    >
      {Object.keys(groupFields)
        .sort()
        .map((groupLabel) => {
          return detailsGroup(groupLabel, groupFields[groupLabel]);
        })}
    </BaseModal>
  );
};

const detailsGroup = (label, fields) => {
  return (
    <Grid container spacing={2}>
      {label && (
        <Grid
          item
          xs={12}
          style={{
            marginTop: 20,
            textTransform: 'uppercase',
            fontWeight: 'bold',
            fontSize: 18,
          }}
        >
          {label}
        </Grid>
      )}
      {fields
        ?.filter((i) => i.type !== columnType.note && i.label !== 'Select All')
        .map((field) => {
          return dynamicField(field);
        })}

      {fields
        ?.filter((field) => field.type === columnType.note)
        .map((field, index) => {
          return (
            <Grid item key={index} xs={12}>
              {noteField(field)}
            </Grid>
          );
        })}
    </Grid>
  );
};

// recursive function
const getElementValue = (element) => {
  if (typeof element === 'string' || element instanceof String) {
    return element;
  }

  if (element?.props && element?.props?.children) {
    return getElementValue(element.props.children);
  }

  return element;
};

const dynamicField = (field) => {
  if (field.type === columnType.list) {
    return listField(field);
  } else if (field.type === columnType.bool) {
    return checkboxField(field);
  }

  let value = field.value;

  if (field.customRender) {
    const element = field.customRender();
    value = getElementValue(element);

    if (!(typeof value === 'string' || value instanceof String)) {
      return customField(field.label, element, DEFAULT_FIELD_SIZE);
    }
  } else if (value === '<nil>') {
    value = '';
  } else {
    value = getColumnValue(field.type, field.value);
  }

  let fieldSize = DEFAULT_FIELD_SIZE;
  if (typeof value === 'string' || value instanceof String) {
    value = value.trim();
  }
  if (value?.length > 60) {
    fieldSize = 12;
  } else if (value?.length > 30) {
    fieldSize = 6;
  }

  return customField(field.label, value, fieldSize);
};

const checkboxField = (field) => {
  return customField(
    field.label,
    field.value ? <IconCheck /> : <IconUncheck />,
    DEFAULT_FIELD_SIZE
  );
};

const customField = (label, element, fieldSize) => {
  return (
    <Grid item key={label} xs={12} sm={fieldSize === 12 ? 12 : 6} md={fieldSize}>
      <Grid wrap="nowrap" container direction="column" justifyContent="space-between">
        <Grid item>
          <InputLabel shrink>{label}</InputLabel>
        </Grid>
        <Grid
          item
          style={{
            borderBottom: '1px solid gray',
            minHeight: 32,
            marginTop: 2,
            fontSize: 16,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {element}
        </Grid>
      </Grid>
    </Grid>
  );
};

const listField = (field) => {
  let list = field?.value?.split(field.listSeparator) || [];
  list = list.filter((value, index) => list.indexOf(value) === index);

  let fieldSize = DEFAULT_FIELD_SIZE;
  if (list?.length > 3) {
    fieldSize = 6;
  }

  return (
    <Grid item key={field.label} xs={12} sm={fieldSize === 12 ? 12 : 6} md={fieldSize}>
      <Select
        multiple
        readOnly
        value={list}
        options={list}
        input={<InputField label={field.label} />}
      />
    </Grid>
  );
};

const noteField = (field) => {
  const firstChar = field?.value?.substring(0, 1);

  if (['{', '[', '<'].includes(firstChar)) {
    let format = '';
    if (['{', '['].includes(firstChar)) {
      format = 'JSON';
    }

    return (
      <TextEditor
        label={field.label}
        setContents={field.value}
        onChange={() => {}}
        format={format}
        value={field.value}
        readOnly
        height={200}
      />
    );
  }

  return (
    <InputField
      label={field.label}
      multiline
      rows={4}
      readOnly
      value={field.value}
      variant="outlined"
    />
  );
};

export default WithBaseModal(RowDetailsModal);
